import type { Emplacement, Famille } from '@whatremains/shared'
import { NOM_FAMILLE } from '@whatremains/shared'

/**
 * Ce qu'une pièce EST, dit en deux mots, partout où elle paraît.
 *
 * ## Pourquoi ça manquait, et pourquoi ça manquait partout
 *
 * Enzo, au marché : « il faudrait que PARTOUT où il y a des items tu ajoutes le
 * type. Là j'ai une "Lame de la troisième levée" et je ne sais pas si c'est une
 * épée ou une dague. Il y a aussi "Bricole de la paie retenue", je ne sais pas
 * à quel emplacement c'est censé aller. »
 *
 * Ses deux exemples demandent deux choses différentes, et c'est pour ça qu'il
 * en faut deux :
 *
 *   - **la famille** répond pour une arme. « Lame » ne dit pas si elle se tient
 *     à une main comme une dague ou occupe la main principale comme une épée,
 *     et pour un rogue cette différence décide de tout le double maniement ;
 *   - **l'emplacement** répond pour le reste. « Plastron » dit son emplacement
 *     tout seul, « Bricole » et « Targe » non.
 *
 * L'information existait depuis toujours dans `NOM_FAMILLE` et
 * `NOM_EMPLACEMENT`, et n'était affichée que dans l'inventaire. Onze écrans
 * montrent des pièces ; les corriger un par un aurait laissé le douzième.
 *
 * Deux écrans n'utilisent PAS ce composant, et c'est voulu : l'inventaire porte
 * déjà l'emplacement en colonne de gauche, et la fiche d'objet dit la prise et
 * les emplacements où la pièce peut aller. Les y ajouter serait une redite. On
 * ne change pas ce qui répond déjà.
 *
 * ## Ce qu'on n'écrit pas
 *
 * Rien pour un matériau ni un consommable : ils portent une famille et un
 * emplacement de REMPLISSAGE — « Ferraille » est rangée en `masse` — et les
 * afficher serait pire que se taire. C'est le même piège que celui des icônes
 * et de la comparaison d'équipement, et il se referme ici de la même façon.
 *
 * ## Pourquoi on écrit TOUJOURS les deux
 *
 * Un premier jet taisait l'emplacement quand la famille semblait le dire :
 * « Plastron · torse » est une redite. La mesure sur le catalogue a montré que
 * l'hypothèse est fausse là où elle compte : **« bouclier » se porte en MAIN
 * GAUCHE**, ce que son nom ne dit pas.
 *
 * L'autre exemple était « brassards », qui se portait dans le DOS. Ce n'était
 * pas une exception à documenter mais une **erreur de nom** : les dix pièces
 * sont des harnais de portage, et la famille s'appelle `harnais` depuis la
 * migration 112. La leçon reste, elle a seulement changé d'exemple — et elle en
 * gagne une seconde : une famille dont l'emplacement surprend mérite d'abord
 * qu'on vérifie son nom.
 *
 * Une redite ne coûte rien ; une ambiguïté coûte un achat pour rien.
 */

/**
 * Où la pièce se porte, dit pour CETTE ligne.
 *
 * `NOM_EMPLACEMENT` sert la colonne de gauche de l'inventaire, où l'on lit
 * l'emplacement seul : « Arme », « Main gauche (moitié) ». Accolés à une
 * famille, ces mots-là tombent mal — « Épée · arme » est une redite, et le
 * « (moitié) » du double maniement n'a rien à faire sur une ligne de marché.
 *
 * `arme_principale` ne dit RIEN : une épée est évidemment une arme, et la main
 * dans laquelle on la met est un choix du joueur, pas une propriété de l'objet.
 * C'est la question d'Enzo — « épée ou dague ? » — à laquelle la famille répond
 * seule.
 */
const OU_CA_SE_PORTE: Partial<Record<Emplacement, string>> = {
  tete: 'tête',
  cou: 'cou',
  torse: 'torse',
  dos: 'dos',
  mains: 'mains',
  taille: 'taille',
  jambes: 'jambes',
  pieds: 'pieds',
  arme_secondaire: 'main gauche',
  anneau_1: 'doigt',
  anneau_2: 'doigt',
}

export function typeDit(o: {
  famille: Famille
  emplacement: Emplacement
  categorie: 'equipement' | 'materiau' | 'consommable'
}): string | null {
  if (o.categorie === 'materiau') return 'Matériau'
  if (o.categorie === 'consommable') return 'Consommable'

  const famille = NOM_FAMILLE[o.famille]
  if (!famille) return null
  const ou = OU_CA_SE_PORTE[o.emplacement]
  return ou ? `${famille} · ${ou}` : famille
}

export function TypeObjet({
  o,
  className = 'objet-type',
}: {
  o: {
    famille: Famille
    emplacement: Emplacement
    categorie: 'equipement' | 'materiau' | 'consommable'
  }
  className?: string
}) {
  const dit = typeDit(o)
  return dit ? <span className={className}>{dit}</span> : null
}
